import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../../shared/core/i18n/use-intl";
import { repeat } from "../../../../../../shared/utils/utils";
import { useRTL } from "../../../../../domain/language/use-rtl";
import { UnstyledButton } from "../../../../common/buttons/default-button";
import { MarkdownText } from "../../../../common/forms/markdown-text";
import { ChevronIcon } from "../../../../common/svg/chevron";
import { theme } from "../../../../styles/theme";

const EnrollmentProgress: React.FC<{ step?: number; stepCount?: number; label?: string; onBack?: () => void }> = ({
  step,
  stepCount,
  label,
  onBack,
}) => {
  const { formatMessage } = useIntl();
  const { isRTL } = useRTL();

  return step !== undefined && step >= 0 && stepCount ? (
    <ProgressContainer>
      {onBack && (
        <BackLink type="button" onClick={onBack}>
          <BackChevron width={10} height={10} />
          {formatMessage("registerScreen.progressBackLink")}
        </BackLink>
      )}
      <ProgressText>
        {formatMessage("registerScreen.progressStepLabel", { step: step + 1, count: stepCount ?? 0 })}
      </ProgressText>
      {!!label && <ProgressSubtitle>{label}</ProgressSubtitle>}
      <ProgressParts $isRTL={isRTL}>
        {repeat(step + 1, (index) => (
          <FilledStep key={`filled ${index}`} bigger={index === step} />
        ))}
        {repeat(stepCount - (step + 1), (index) => (
          <EmptyStep key={`empty ${index}`} />
        ))}
      </ProgressParts>
    </ProgressContainer>
  ) : null;
};

export default EnrollmentProgress;

const ProgressContainer = styled.div`
  align-self: stretch;
  margin-bottom: 40px;
`;

const BackLink = styled(UnstyledButton)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  font-size: 0.8125rem;
  color: ${theme.colors.blue[700]};
`;

/* The chevron points back: the icon's own path faces forward. */
const BackChevron = styled(ChevronIcon)`
  fill: ${theme.colors.blue[700]};
  transform: rotate(180deg);
`;

const ProgressSubtitle = styled.div`
  margin-top: 2px;
  font-size: 0.75rem;
  color: ${theme.colors.gray[800]};
`;

const ProgressText = styled(MarkdownText)`
  ${theme.boldText};
  font-size: 1rem;
  color: ${theme.colors.blue[800]};
`;

const ProgressParts = styled.div<{ $isRTL: boolean }>`
  display: flex;
  flex-direction: row;
  height: 5px;
  margin-top: 15px;

  > *:not(:first-child) {
    margin-left: ${(props) => (props.$isRTL ? 0 : 10)}px;
    margin-right: ${(props) => (props.$isRTL ? 10 : 0)}px;
  }
`;

const EmptyStep = styled.div`
  display: flex;
  width: 5px;
  height: 5px;
  flex-grow: 2;
  flex-shrink: 0;
  border-radius: 5px;
  background: #b1b1b1;
`;

const FilledStep = styled(EmptyStep)<{ bigger?: boolean }>`
  flex-grow: ${(props) => (props.bigger ? 3 : 2)};
  background: #81c241;
`;
